import { buildMemoSections, type MemoSection } from "@/lib/memoSections";
import type { MemoBlock, MemoEntry } from "@/lib/types";

/**
 * A point ADDED this meeting — i.e. new since the last Protokoll — and not ticked
 * off. Mirrors the backend `is_new_this_meeting` so the "Nur Neues" toggle and the
 * finalize email can never disagree: a `point` (not a Zwischenüberschrift, not an
 * `update` line), with no `carried_from_entry_id` (a carried-forward point
 * references its predecessor), not retired (we surface what was added, not what was
 * ticked off), and not a merged-in historical row.
 */
export function isNewThisMeeting(entry: MemoEntry): boolean {
  return (
    entry.kind === "point" && !entry.carried_from_entry_id && !entry.retired && !entry.is_historical
  );
}

/**
 * The block's sections with entries filtered to the new points, dropping any
 * section left empty. The heading is kept so the new points still render under
 * their Zwischenüberschrift; a leading run keeps its `heading: null`.
 */
export function newSectionsForBlock(block: MemoBlock): MemoSection[] {
  return buildMemoSections(block)
    .map((section) => ({ ...section, entries: section.entries.filter(isNewThisMeeting) }))
    .filter((section) => section.entries.length > 0);
}

/** True when any block has at least one new point (drives the empty state). */
export function hasNewEntries(blocks: MemoBlock[]): boolean {
  return blocks.some((block) => newSectionsForBlock(block).length > 0);
}

/** Total number of new points across all blocks (drives the toggle's count badge). */
export function countNewEntries(blocks: MemoBlock[]): number {
  return blocks.reduce(
    (n, block) =>
      n + newSectionsForBlock(block).reduce((m, section) => m + section.entries.length, 0),
    0,
  );
}
